class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.overlay = this.querySelector('[id^="CartDrawer-Overlay"]');
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.open();
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.close();
      });
    }
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.cart-icon--drawer');
    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Slajd robi CSS (data-drawer-direction). Tu sterujemy tylko stanem klas.
    this.classList.remove('closing');
    this.classList.add('open');

    // Wymuś flush stylu (reflow), by zarejestrować off-state, potem dodaj .active —
    // synchronicznie, więc działa też gdy rAF jest wstrzymany (zastępuje setTimeout(0),
    // który "nie zawsze odpalał animację").
    void this.offsetWidth;
    this.classList.add('animate', 'active');

    this._afterDrawerTransition(() => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    });

    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  async close() {
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');

    // .closing cofa treść do stanu zamkniętego; host widoczny do końca przejścia.
    this.classList.add('closing');

    await this._drawerTransitionDone();

    this.classList.remove('open', 'active', 'animate', 'closing');
  }

  /* ─── Animacja: czekanie na koniec przejścia treści (jak w SideDrawer) ─── */
  _drawerContent() {
    return this.querySelector('[data-drawer-content]');
  }

  _afterDrawerTransition(callback) {
    this._drawerTransitionDone().then(callback);
  }

  _drawerTransitionDone() {
    return new Promise((resolve) => {
      const content = this._drawerContent();
      if (!content) {
        resolve();
        return;
      }
      let settled = false;
      let timer = null;
      const finish = () => {
        if (settled) return;
        settled = true;
        content.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        resolve();
      };
      const onEnd = (event) => {
        if (event.target === content && (event.propertyName === 'transform' || event.propertyName === 'opacity')) {
          finish();
        }
      };
      content.addEventListener('transitionend', onEnd);
      timer = setTimeout(finish, 360);
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    const cartItemsContainer = this.querySelector('#CartDrawer-CartItems');
    const popularProductsBlock = cartItemsContainer?.querySelector('.cart-drawer__recommendations');
    const preservedPopularProducts = popularProductsBlock ? popularProductsBlock.outerHTML : null;
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    if (preservedPopularProducts) {
      const newCartItemsContainer = this.querySelector('#CartDrawer-CartItems');
      const newPopularProductsBlock = newCartItemsContainer?.querySelector('.cart-drawer__recommendations');
      if (!newPopularProductsBlock && newCartItemsContainer) {
        const statusElements = newCartItemsContainer.querySelectorAll('p[role="status"]');
        if (statusElements.length > 0) {
          statusElements[0].insertAdjacentHTML('beforebegin', preservedPopularProducts);
        } else {
          newCartItemsContainer.insertAdjacentHTML('beforeend', preservedPopularProducts);
        }
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class GiftWrapToggle extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="checkbox"]');
    this.spinner = this.querySelector('.cart-gift-wrap-toggle__spinner');
    this.input?.addEventListener('change', this.onChange.bind(this));
  }

  async onChange(event) {
    const checked = event.target.checked;
    const variantId = this.dataset.variantId;
    const sectionId = this.dataset.sectionId;

    if (!variantId) return;

    this.setLoading(true);
    try {
      if (checked) {
        await this.addGiftWrap(variantId, sectionId);
      } else {
        await this.removeGiftWrap(variantId, sectionId);
      }
    } catch (e) {
      console.error('GiftWrapToggle error:', e);
      // Revert checkbox on failure
      this.input.checked = !checked;
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.classList.toggle('loading', loading);
    this.spinner?.classList.toggle('hidden', !loading);
    this.input.disabled = loading;
  }

  async addGiftWrap(variantId, sectionId) {
    const config = fetchConfig('json');
    const response = await fetch(`${routes.cart_add_url}`, {
      ...config,
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        ...(sectionId ? { sections: [sectionId, 'cart-icon-bubble'] } : {}),
      }),
    });
    const data = await response.json();
    document.dispatchEvent(new CustomEvent('GiftWrapUpdateEvent', { detail: { data, id: this.id } }));
    this.applySections(data.sections);
  }

  async removeGiftWrap(variantId, sectionId) {
    const config = fetchConfig('json');
    const response = await fetch(`${routes.cart_change_url}`, {
      ...config,
      body: JSON.stringify({
        id: variantId,
        quantity: 0,
        ...(sectionId ? { sections: [sectionId, 'cart-icon-bubble'] } : {}),
      }),
    });
    const data = await response.json();
    document.dispatchEvent(new CustomEvent('GiftWrapUpdateEvent', { detail: { data, id: this.id } }));
    this.applySections(data.sections);
  }

  applySections(sections) {
    if (!sections) return;
    const cartItemsComponent =
      document.querySelector('cart-drawer-items') ||
      document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.updateSections === 'function') {
      cartItemsComponent.updateSections(sections);
    }
  }
}

if (!customElements.get('gift-wrap-toggle')) {
  customElements.define('gift-wrap-toggle', GiftWrapToggle);
}

class CartItemVariantSelect extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.line = parseInt(this.dataset.line, 10);
    this.sectionId = this.dataset.sectionId;
    this.currentVariantId = this.dataset.currentVariant;
    this.select?.addEventListener('change', this.onChange.bind(this));
  }

  async onChange(event) {
    const newVariantId = parseInt(event.target.value, 10);
    if (!newVariantId || newVariantId === parseInt(this.currentVariantId, 10)) return;

    this.classList.add('loading');
    this.select.disabled = true;

    try {
      // Read current line quantity + properties before removal
      const cartBefore = await fetch('/cart.js').then((r) => r.json());
      const currentLine = cartBefore.items[this.line - 1];
      const quantity = currentLine?.quantity || 1;
      const properties = currentLine?.properties || {};
      const sellingPlan = currentLine?.selling_plan_allocation?.selling_plan?.id;

      const config = fetchConfig('json');

      // Step 1: remove old line
      await fetch(`${routes.cart_change_url}`, {
        ...config,
        body: JSON.stringify({ line: this.line, quantity: 0 }),
      });

      // Step 2: add new variant with same quantity / properties
      const addBody = {
        items: [
          {
            id: newVariantId,
            quantity,
            ...(Object.keys(properties).length ? { properties } : {}),
            ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
          },
        ],
        sections: [this.sectionId, 'cart-icon-bubble'],
      };

      const addResponse = await fetch(`${routes.cart_add_url}`, {
        ...config,
        body: JSON.stringify(addBody),
      });
      const addData = await addResponse.json();

      if (addData.status && addData.status >= 400) {
        throw new Error(addData.description || 'Cart add failed');
      }

      const cartItemsComponent =
        document.querySelector('cart-drawer-items') ||
        document.querySelector('cart-items-component');
      if (cartItemsComponent && typeof cartItemsComponent.updateSections === 'function' && addData.sections) {
        cartItemsComponent.updateSections(addData.sections);
      }
    } catch (e) {
      console.error('CartItemVariantSelect error:', e);
      this.select.value = this.currentVariantId;
    } finally {
      this.classList.remove('loading');
      this.select.disabled = false;
    }
  }
}

if (!customElements.get('cart-item-variant-select')) {
  customElements.define('cart-item-variant-select', CartItemVariantSelect);
}
